import { ActionIcon, Button, Group, Menu, Modal, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { useEffect } from 'react';
import QrCode from 'react-qr-code';
import { FaFileExport, FaFilter, FaPen, FaPlus, FaQrcode, FaTrash } from 'react-icons/fa';
import InputField, { InputFieldTypes } from '../components/input_field';
import SimpleHeader from '../components/simple_header';
import Table from '../components/table';
import { useApp } from '../contexts/app.context';
import { DELETE_VEHICLE, GET_ALL_VEHICLES, SAVE_VEHICLE, SAVE_VEHICLE_DEVICE } from '../services/vehicles';
import { formatAddress, getDeviceTitle, getVehicleTitle, vehicleTypes } from '../utility/util';
import { GET_ALL_DEVICES } from '../services/devices';
import { GET_ALL_USERS } from '../services/users';
import { useAuth } from '../contexts/auth.context';
import { Buffer } from 'buffer';

export default function VehiclesPage(){
    const [items, setItems] = React.useState<any[]>([]);
    const [modal, setModal] = React.useState<any>(null);
    const [loadingItems, setLoadingItems] = React.useState<boolean>(false);
    const [loadingExporting, setLoadingExporting] = React.useState<boolean>(false);
    const [loadingDelete, setLoadingDelete] = React.useState({});
    const [children, setChildren] = React.useState(false);
    const [vehicleDevice, setVehicleDevice] = React.useState(null);
    const [modalQrcode, setModalQrcode] = React.useState(null);
    const [filters, setFilters] = React.useState({ title: "" });

    const filteredItems = items.filter(i => (
        (i.license_plate ?? "").toLocaleLowerCase().includes(filters.title.toLocaleLowerCase()) ||
        (i.model ?? "").toLocaleLowerCase().includes(filters.title.toLocaleLowerCase()) ||
        (i.brand ?? "").toLocaleLowerCase().includes(filters.title.toLocaleLowerCase())
    ))

    const app = useApp();
    const {currentCompany} = useAuth();

    const loadItems = () => {
        setLoadingItems(true)
        GET_ALL_VEHICLES({ children })
        .then(res => {
            setItems(res);
            setLoadingItems(false)
        })
        .catch(err => {
            setLoadingItems(false)
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }
    
    const deleteItem = (vehicleId) => {
        setLoadingDelete(ld => ({...ld, [`${vehicleId}`]: true}))
        DELETE_VEHICLE(vehicleId)
        .then(res => {
            setLoadingDelete(ld => ({...ld, [`${vehicleId}`]: false}))
            loadItems()
        })
        .catch(err => {
            setLoadingDelete(ld => ({...ld, [`${vehicleId}`]: false}))
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }


    const handleExport = () => {
        setLoadingExporting(true);

        let csvText = [
            [
                "Empresa",
                "Clientes",
                "Placa",
                "Marca",
                "Modelo",
                "Ano",
                "Ano do Modelo",
                "Cor",
                "Chassi",
                "Endereço",
                "Data Cadastro",
                "Dispositivo - Modelo",
                "Dispositivo - IMEI",
                "Dispositivo - Nº",
                "Dispositivo - SSID",
            ],
            ...items.map(item => [
                (item?.company?.name),
                (item?.customers ?? []).map(c => c.name ?? "").join(", "),
                item?.license_plate,
                item?.brand,
                item?.model,
                item?.year,
                item?.model_year,
                item?.color,
                item?.chassi,
                item?.address,
                item?.createdAt,
                (item?.devices ?? [])[0]?.device?.model?.title,
                (item?.devices ?? [])[0]?.device?.props?.DEV_ID,
                (item?.devices ?? [])[0]?.device?.props?.CHIP_NO,
                (item?.devices ?? [])[0]?.device?.props?.SSID_CHIP,
            ])
        ].map(items => items.map(it => `"${it ?? ""}"`).join(",")).join("\n")
    
        const byteCharacters = Buffer.from(csvText, "utf-8");
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) { byteNumbers[i] = byteCharacters[i]; }
        const byteArray = new Uint8Array(byteNumbers);
    
        const blob = new Blob([byteArray], { type: "application/csv" });
    
        const url = URL.createObjectURL(blob);;
        const a = document.createElement('a');
        a.href = url;
        a.download = `Veiculos_Satify.csv`;
        a.click();
        URL.revokeObjectURL(url);

        setLoadingExporting(false);
    }

    useEffect(loadItems, [children]);

    return <>
        <SimpleHeader
            title="Veículos"
            right={
                <Menu>
                    <Menu.Target>
                        <ActionIcon color="orange" variant='filled' size="lg"><FaFilter /></ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <InputField
                            name="title"
                            value={filters.title}
                            onChange={({title}) => setFilters(f => ({ ...f, title }))}
                            placeholder='Buscar'
                            size="md"
                        />
                        <InputField
                            name="title"
                            value={children}
                            fieldType={InputFieldTypes.SWITCH}
                            onChange={() => setChildren(c => !c)}
                            title='Ver todos'
                            size="md"
                        />
                    </Menu.Dropdown>
                </Menu>
            }
            buttons={[
                !["Cliente", "Agente", "Motorista"].includes(currentCompany?.role?.profile?.title) &&
                {leftIcon: <FaPlus />, title: "Veículo", onClick: () => { setModal(true)}},
                {leftIcon: <FaFileExport />, loading: loadingExporting, disabled: loadingItems, title: `Exportar`, onClick: () => { handleExport() }},
            ].filter(nn => nn)}
        />
        
        <Table
            data={filteredItems}
            loading={loadingItems}
            columns={[
                { key: "company", title: "Empresa", render: item => item?.company?.name },
                { key: "customers", title: "Usuários", render: item => <div>
                    {(item.customers || []).map(c => <div>{c.name}</div>)}
                </div> },
                { key: "type", title: "Tipo", render:  (item) => getVehicleTitle(item.type) },
                { key: "license_plate", title: "Placa" },
                { key: "brand", title: "Marca" },
                { key: "model", title: "Modelo" },
                { key: "year", title: "Ano" },
                { key: "model_year", title: "Ano do modelo" },
                { key: "color", title: "Cor do veículo" },
                { key: "chassi", title: "Chassi" },
                { key: "address", title: "Última Localização", style: { minWidth: 300 }, render: (item) => <Text size="xs">{formatAddress((item.devices ?? [])[0]?.device?.lastHistory?.props)}</Text> },
                !["Cliente", "Agente", "Motorista"].includes(currentCompany?.role?.profile?.title) && { key: "device", title: "Dispositivo", render: (item) => <>
                    <Button size="sm" variant="outline" onClick={() => setVehicleDevice({deviceId: item?.device?._id, vehicleId: item._id})}>
                        {(item.devices || []).length === 0 ? "Vincular" : getDeviceTitle(item.devices[0].device)}
                    </Button>
                </> },
            ].filter(nn => nn)}
            actions={[
                {
                    color: "blue",
                    title: "Baixar QRCode",
                    leftIcon: <FaQrcode />,
                    onClick: ({item}) => { setModalQrcode(item); }
                },
                !["Cliente", "Agente", "Motorista"].includes(currentCompany?.role?.profile?.title) && {
                    color: "gray",
                    title: "Editar",
                    leftIcon: <FaPen />,
                    onClick: ({item}) => { setModal(item); }
                },
                !["Cliente", "Agente", "Motorista"].includes(currentCompany?.role?.profile?.title) && {
                    color: "red",
                    leftIcon: <FaTrash />,
                    title: "Deletar",
                    loading: item => Boolean(loadingDelete[item._id]), 
                    onClick: ({item}) => {
                        app.confirmDialog(
                            {text: "Realmente deseja deletar?"},
                            (({confirmed}) => {
                                if(confirmed) deleteItem(item._id);
                            })
                        )
                    }
                },
            ].filter(nn => nn)}
        />

        <VehicleModal
            data={modal}
            opened={!!modal}
            onClose={() => setModal(null)}
            onSave={() => { loadItems(); setModal(null) }}
        />
        
        <VehicleDevice
            deviceOpt={true}
            vehicleOpt={false}
            vehicleId={vehicleDevice?.vehicleId}
            deviceId={vehicleDevice?.deviceId}
            opened={!!vehicleDevice}
            onClose={() => setVehicleDevice(null)}
            onSave={() => { loadItems(); setVehicleDevice(null) }}
        />

        <Modal size="md" opened={!!modalQrcode} onClose={() => setModalQrcode(null)} styles={{content: {textAlign: 'center'}}}>
            {modalQrcode && <QrCode size={300} value={JSON.stringify({vehicle: modalQrcode._id})} />}
        </Modal>
    </>
}

const VehicleModal = (props) => {
    const { data : dataParent = null, onClose, onSave, opened = false } = props;
    const [loadingSave, setLoadingSave] = React.useState<boolean>(false);
    const [data, setData] = React.useState<any>(null);
    const [customers, setCustomers] = React.useState<any>([]);

    const handleChange = dt => setData(d => ({...d, ...dt}));

    const handleSave = () => {
        setLoadingSave(true)
        SAVE_VEHICLE({ ...data, id: dataParent?._id })
        .then(() => {
            setLoadingSave(false)
            onSave && onSave();
        })
        .catch(err => {
            setLoadingSave(false)
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }

    const searchLicensePlate = () => {
        if(data.license_plate){
            // sinespApi.search('AAA111')
            // .then(res => {
            //     alert(JSON.stringify(res))
            // })
            // .catch((err) => {
            //     showNotification({title: "Ops", message: err.message, color: 'red'})
            // })
            // alert(data.license_plate)

        }
    }

    const loadDevices = () => {
        GET_ALL_USERS()
        .then((res) => {
            setCustomers(res)
        })
        .catch(err => {
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }

    useEffect(() => {
        loadDevices()
    }, [])

    useEffect(() => {
        if(opened) setData({...dataParent, customers: (dataParent?.customers || []).map(c => c._id)})
    }, [opened, dataParent])

    return <>
        <Modal
            opened={!!opened}
            onClose={onClose}
            size="md"
        >
            <InputField
                mb="lg"
                name="customers"
                title="Usuários"
                fieldType={InputFieldTypes.SELECT}
                multiple
                value={data?.customers}
                onChange={handleChange}
                options={customers.map(c => ({value: c._id, label: c.name}))}
            />
            <InputField
                mb="lg"
                name="type"
                title="Tipo do veículo"
                fieldType={InputFieldTypes.SELECT}
                value={data?.type}
                options={vehicleTypes}
                onChange={handleChange}
            />
            <InputField
                mb="lg"
                name="license_plate"
                title="Placa *"
                value={data?.license_plate}
                onChange={handleChange}
                onBlur={searchLicensePlate}
            />
            <InputField
                mb="lg"
                name="brand"
                title="Marca *"
                value={data?.brand}
                onChange={handleChange}
            />
            <InputField
                mb="lg"
                name="model"
                title="Modelo *"
                value={data?.model}
                onChange={handleChange}
            />
            <InputField
                mb="lg"
                name="year"
                title="Ano"
                value={data?.year}
                onChange={handleChange}
            />
            <InputField
                mb="lg"
                name="model_year"
                title="Ano do modelo"
                value={data?.model_year}
                onChange={handleChange}
            />
            <InputField
                mb="lg"
                name="color"
                title="Cor do veículo"
                value={data?.color}
                onChange={handleChange}
            />
            <InputField
                mb="lg"
                name="chassi"
                title="Chassi"
                value={data?.chassi}
                onChange={handleChange}
            />

            <Group position='right'>
                <Button variant='subtle' disabled={loadingSave} onClick={onClose}>Cancelar</Button>
                <Button variant='filled' loading={loadingSave} onClick={handleSave}>Salvar</Button>
            </Group>
        </Modal>
    </>
}

export const VehicleDevice = (props) => {
    const { vehicleId: vehicleIdParent, deviceId: deviceIdParent, onClose, onSave, opened = false, deviceOpt = true, vehicleOpt = true } = props;
    const [loadingSave, setLoadingSave] = React.useState<boolean>(false);
    const [vehicleId, setVehicleId] = React.useState<any>(null);
    const [deviceId, setDeviceId] = React.useState<any>(null);
    const [devices, setDevices] = React.useState<any[]>([]);
    const [vehicles, setVehicles] = React.useState<any[]>([]);

    const handleSave = () => {
        setLoadingSave(true)
        SAVE_VEHICLE_DEVICE(vehicleId, deviceId)
        .then(() => {
            setLoadingSave(false)
            onSave && onSave();
        })
        .catch(err => {
            setLoadingSave(false)
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }

    const loadDevices = () => {
        GET_ALL_DEVICES()
        .then((res) => {
            setDevices(res)
        })
        .catch(err => {
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }
    
    const loadVehicles = () => {
        GET_ALL_VEHICLES()
        .then((res) => {
            setVehicles(res)
        })
        .catch(err => {
            showNotification({title: "Ops", message: err.message, color: 'red'})
        })
    }

    useEffect(() => {
        if(opened) setDeviceId(deviceIdParent)
    }, [opened, deviceIdParent])
    
    useEffect(() => {
        if(opened) setVehicleId(vehicleIdParent)
    }, [opened, vehicleIdParent])
    
    useEffect(() => {
        loadDevices()
        loadVehicles()
    }, [])

    return <>
        <Modal
            opened={!!opened}
            onClose={onClose}
            size="md"
            styles={{content: {minHeight: 400}}}
        >
            <InputField
                mb="lg"
                name="vl"
                title="Dispositivo"
                disabled={!deviceOpt}
                value={deviceId}
                fieldType={InputFieldTypes.SELECT}
                options={devices.map(d => ({ value: d._id, label: getDeviceTitle(d) }))}
                onChange={({vl}) => setDeviceId(vl)}
            />
            <InputField
                mb="lg"
                name="vl"
                title="Veículo"
                disabled={!vehicleOpt}
                value={vehicleId}
                fieldType={InputFieldTypes.SELECT}
                options={vehicles.map(v => ({ value: v._id, label: v.license_plate }))}
                onChange={({vl}) => setVehicleId(vl)}
            />
            <Text color="gray" size="xs">Ao vincular o dispositivo à um veículo ele será desvinculado caso esteja associado à algum outro</Text>
            <Group position='right'>
                <Button variant='subtle' disabled={loadingSave} onClick={onClose}>Cancelar</Button>
                <Button variant='filled' loading={loadingSave} onClick={handleSave}>Salvar</Button>
            </Group>
        </Modal>
    </>
}